<template>
  <v-card
    class="integration-card"
    rounded
    outlined
    flat
    role="article"
    tabindex="0"
    @click="handleServiceEdit"
    @keydown.enter="handleServiceEdit"
    @keydown.space.prevent="handleServiceEdit"
    :aria-label="`Integration: ${getServiceName()}, Status: ${getStatusText()}, Last run: ${getLastRunAriaLabel()}`"
    :data-testid="`integration-card-${integrationService.id}`"
  >
    <div class="landscape-layout" :class="`landscape-layout--${getStatusIconColor()}`">
      <!-- Left Section: Sparkline with Stats -->
  <div v-if="hasSparklineData" class="sparkline-section" role="img" :aria-label="getSparklineAriaLabel()">
        <v-sheet
          class="sparkline-container"
          :class="`sparkline-container--${getStatusIconColor()}`"
          :data-testid="`sparkline-${integrationService.id}`"
        >
          <div class="sparkline-chart-area">
    <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-sparkline
                  v-bind="attrs"
                  v-on="on"
                  :value="sparklineValues"
                  color="#666666"
                  :height="30"
                  :width="160"
                  type="trend"
                  :auto-draw="false"
                  :line-width="1"
                  :padding="4"
                  :smooth="false"
                  class="grey-sparkline"
                  :aria-hidden="true"
                ></v-sparkline>
              </template>
              <span>{{ getSparklineTooltip() }}</span>
            </v-tooltip>
          </div>

          <!-- Stats Row -->
          <div
            class="sparkline-stats"
            role="group"
            :aria-label="'Performance statistics for ' + getServiceName()"
            :data-testid="`stats-${integrationService.id}`"
          >
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <div class="sparkline-stat-item" v-bind="attrs" v-on="on">
                  <div class="sparkline-stat-value" :class="successCount > 0 ? 'success--text' : 'grey--text'">{{ successCount }}</div>
                  <div class="sparkline-stat-label">Success</div>
                </div>
              </template>
              <span>Successful operations in the last 24 hours</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <div class="sparkline-stat-item" v-bind="attrs" v-on="on">
                  <div class="sparkline-stat-value" :class="failureCount > 0 ? 'error--text' : 'grey--text'">{{ failureCount }}</div>
                  <div class="sparkline-stat-label">Failures</div>
                </div>
              </template>
              <span>Failed operations in the last 24 hours</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <div class="sparkline-stat-item" v-bind="attrs" v-on="on">
                  <div class="sparkline-stat-value" :class="localEnabled ? 'primary--text' : 'grey--text'">{{ successRate }}</div>
                  <div class="sparkline-stat-label">Rate</div>
                </div>
              </template>
              <span>Success rate percentage over the last 24 hours</span>
            </v-tooltip>
          </div>
        </v-sheet>
      </div>

      <!-- Middle Section: Content -->
  <div class="content-section" :class="contentSectionClasses">
        <!-- Accessibility note when no sparkline data is present -->
        <div v-if="!hasSparklineData" class="sr-only" aria-live="polite">
          No activity data available for this service.
        </div>
        <!-- Header with title -->
        <div class="content-header">
          <div class="title-section">
            <div class="service-name-row">
              <div class="service-title-container">
                <div class="service-name-container">
                  <div class="service-name" :data-testid="`integration-name-${integrationService.id}`">
                    {{ getServiceName() }}
                  </div>
                  <!-- Status indicator: Last Run or Disabled -->
                  <div class="last-run-indicator">
                    <v-icon
                      class="last-run-icon"
                      small
                      :aria-hidden="true"
                    >
                      {{ localEnabled ? 'mdi-check-circle-outline' : 'mdi-pause-circle-outline' }}
                    </v-icon>
                    <span
                      class="last-run-text"
                      :data-testid="`last-run-${integrationService.id}`"
                      :aria-label="localEnabled ? 'Last execution: ' + getLastRunText() : 'Integration disabled'"
                    >
                      {{ localEnabled ? getLastRunText() : 'Disabled' }}
                    </span>
                  </div>
                </div>
                <div v-if="integrationService.notes" class="service-description" :data-testid="`integration-description-${integrationService.id}`">
                  {{ getTruncatedNotes() }}
                </div>
              </div>
            </div>
          </div>
          <!-- Actions Menu -->
          <div class="content-actions">
            <v-menu bottom left>
              <template #activator="{ on, attrs }">
                <v-btn icon x-small v-bind="attrs" v-on="on" :aria-label="'Actions menu for ' + getServiceName()" :data-testid="`integration-menu-${integrationService.id}`">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
              <v-list dense role="menu" :aria-label="'Actions for ' + getServiceName()">
                <v-list-item
                  v-if="userHasFullConnectorServicePermission && integrationService.isEditable"
                  @click="handleServiceEdit"
                  role="menuitem"
                  :data-testid="`edit-service-${integrationService.id}`"
                >
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-else
                  @click="handleServiceEdit"
                  role="menuitem"
                  :data-testid="`view-service-${integrationService.id}`"
                >
                  <v-list-item-title>View</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="userHasFullConnectorServicePermission" @click="handleServiceExport" role="menuitem" :data-testid="`export-service-${integrationService.id}`">
                  <v-list-item-title>Export</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="userHasFullConnectorServicePermission" @click="handleServiceClone" role="menuitem" :data-testid="`clone-service-${integrationService.id}`">
                  <v-list-item-title>Clone</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="integrationService.failures?.length > 0 && userHasLogViewPermission" @click="handleViewFailures" role="menuitem" :data-testid="`view-failures-${integrationService.id}`">
                  <v-list-item-title>View Failure Logs</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="userHasFullConnectorServicePermission"
                  :disabled="!integrationService.isEditable"
                  @click.capture.stop.prevent="integrationService.isEditable ? handleDeleteService() : null"
                  role="menuitem"
                  :data-testid="`delete-service-${integrationService.id}`"
                >
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
    <!-- HTTP Method and URL with Switch -->
  <div class="url-section">
          <div v-if="showEndpoint" class="url-group" role="group" :aria-label="'HTTP endpoint: ' + getHttpMethod() + ' ' + integrationService.requestUrl" :data-testid="`endpoint-${integrationService.id}`">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <div
                  class="http-method-chip http-method-primary"
                  v-bind="attrs"
                  v-on="on"
                  :aria-label="`HTTP method: ${getHttpMethod()}`"
                >
                  {{ getHttpMethod() }}
                </div>
              </template>
              <span>HTTP method for this service endpoint</span>
            </v-tooltip>
            <div class="url-field">
              <span class="url-text" :title="integrationService.requestUrl" :aria-label="'Endpoint URL: ' + integrationService.requestUrl">
                {{ integrationService.requestUrl }}
              </span>
            </div>
          </div>
          <div v-else class="s3-indicator-wrapper">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <div class="s3-indicator" v-bind="attrs" v-on="on" role="note" :aria-label="'Direct to S3 mode'" :data-testid="`endpoint-s3-${integrationService.id}`">
                  <v-icon color="primary" size="28" aria-hidden="true">mdi-cloud-upload-outline</v-icon>
                  <div class="text-subtitle-2 ml-1">Direct to S3</div>
                </div>
              </template>
              <span>Direct to S3 mode doesn't use an HTTP endpoint</span>
            </v-tooltip>
          </div>

          <!-- Switch -->
          <div class="url-switch-container" @click.stop>
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-switch
                  v-model="localEnabled"
                  :disabled="!integrationService.isEditable || !userHasFullConnectorServicePermission"
                  color="primary"
                  inset
                  dense
                  class="integration-switch cassie-custom-toggle"
                  :class="{ 'toggle-on': localEnabled, 'toggle-off': !localEnabled }"
                  v-bind="attrs"
                  v-on="on"
                  @change="handleToggleEnabled"
                  :aria-label="`${localEnabled ? 'Disable' : 'Enable'} service: ${getServiceName()}`"
                  :data-testid="`integration-toggle-${integrationService.id}`"
                  role="switch"
                  :aria-checked="localEnabled"
                ></v-switch>
              </template>
              <span>{{ localEnabled ? 'Disable' : 'Enable' }} this service</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue'
import type { PropType } from 'vue'
import type { IntegrationService, SparklineDataPoint } from './types'

export default {
  name: 'IntegrationCard',


  props: {
    /** Integration service data to display */
    integrationService: {
      type: Object as PropType<IntegrationService>,
      required: true
    },
    userHasLogViewPermission: {
      type: Boolean,
      default: false
    },
    userHasFullConnectorServicePermission: {
      type: Boolean,
      default: false
    },
    showEndpoint: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      /** Local enabled state for immediate UI feedback */
      localEnabled: this.integrationService.isEnabled,
    }
  },

  watch: {
    /** Update local state when prop changes */
    'integrationService.isEnabled'(newValue: boolean) {
      this.localEnabled = newValue
    }
  },

  computed: {
    /** Check if stats object is provided */
    hasStats(): boolean {
      return !!this.integrationService.stats
    },

    /** Get sparkline values for chart display */
    sparklineValues(): number[] {
      return this.integrationService.stats?.sparklineData?.map((point: SparklineDataPoint) => point.success + point.failure) || []
    },

    /** Check if sparkline has meaningful data */
    hasSparklineData(): boolean {
      if (!this.integrationService.stats?.sparklineData) return false
      const has24hStats = (this.successCount + this.failureCount) > 0
      const values = this.sparklineValues
      const hasSparklineActivity = values.length > 0 && values.some(value => value > 0)
      return has24hStats || hasSparklineActivity
    },

    /** Get success count from stats */
    successCount(): number {
      return this.integrationService.stats?.stats24h?.success || 0
    },

    /** Get failure count from stats */
    failureCount(): number {
      return this.integrationService.stats?.stats24h?.failure || 0
    },

    /** Calculate success rate percentage */
    successRate(): string {
      const total = this.successCount + this.failureCount
      if (total === 0) return 'n/a'
      return Math.round((this.successCount / total) * 100) + '%'
    },

    /** Get current status - centralized logic */
    currentStatus(): string {
      if (!this.localEnabled) return 'disabled'
      return this.hasStats
        ? this.integrationService.stats?.status || 'healthy'
        : this.integrationService.statusHint || 'healthy'
    },

    /** Dynamic classes for content section */
    contentSectionClasses(): Record<string, boolean> {
      return {
  'content-section--success-border': this.localEnabled && !this.hasSparklineData,
  'content-section--grey-border': !this.localEnabled && !this.hasSparklineData
      }
    }
  },

  methods: {
    /** Handle toggle enabled switch change */
    handleToggleEnabled(value: boolean) {
      this.localEnabled = value
      this.$emit('change:active', { ...this.integrationService, isEnabled: this.localEnabled })
    },

    handleServiceEdit () {
      this.$emit('edit-service', this.integrationService)
    },
    handleServiceExport () {
      this.$emit('export-service', this.integrationService)
    },

    handleServiceClone () {
      this.$emit('clone-service', this.integrationService)
    },

    handleViewFailures () {
      this.$emit('view-failures', this.integrationService)
    },

    handleDeleteService () {
      this.$emit('delete-service', this.integrationService)
    },

    /** Get the service name for the main title */
    getServiceName(): string {
      return this.integrationService.name || 'Unnamed Service'
    },

    /** Get status icon based on current state */
    getStatusIcon(): string {
      if (!this.localEnabled) return 'mdi-pause-circle'

      switch (this.currentStatus) {
        case 'healthy': return 'mdi-check-circle'
        case 'runningWithErrors': return 'mdi-alert-circle'
        case 'disabled': return 'mdi-pause-circle'
        default: return 'mdi-help-circle'
      }
    },

    /** Get status icon color */
    getStatusIconColor(): string {
      if (!this.localEnabled) return 'grey'

      switch (this.currentStatus) {
        case 'healthy': return 'success'
        case 'runningWithErrors': return 'warning'
        case 'disabled': return 'grey'
        default: return 'grey'
      }
    },

    /** Get last run text with relative time */
    getLastRunText(): string {
      if (!this.integrationService.stats) return 'Enabled'
      if (!this.integrationService.stats?.lastRunAt) return 'Never run'

      const lastRun = new Date(this.integrationService.stats.lastRunAt)
      const now = new Date()
      const diffMs = now.getTime() - lastRun.getTime()
      const diffMins = Math.floor(diffMs / (1000 * 60))

      if (diffMins < 1) return 'Just ran'
      if (diffMins < 60) return `Ran ${diffMins}m ago`
      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return `Ran ${diffHours}h ago`
      const diffDays = Math.floor(diffHours / 24)
      return `Ran ${diffDays}d ago`
    },

    /** Get HTTP method for the service from data */
    getHttpMethod(): string {
      return this.integrationService.httpMethod || 'POST'
    },

    /** Get status text for accessibility */
    getStatusText(): string {
      if (!this.localEnabled) return 'disabled'

      switch (this.currentStatus) {
        case 'healthy': return 'healthy'
        case 'runningWithErrors': return 'running with errors'
        case 'disabled': return 'disabled'
        default: return 'unknown'
      }
    },

    /** Get sparkline ARIA label for accessibility */
    getSparklineAriaLabel(): string {
  const total = this.successCount + this.failureCount
  return `Activity chart showing ${total} total operations with ${this.successCount} successes and ${this.failureCount} failures over 24 hours`
    },

    /** Get sparkline tooltip text */
    getSparklineTooltip(): string {
      const total = this.successCount + this.failureCount
      return `24-hour activity: ${total} operations (${this.successCount} successful, ${this.failureCount} failed)`
    },

    /** Get truncated notes (first 50 characters) */
    getTruncatedNotes(): string {
      if (!this.integrationService.notes) return ''
      return this.integrationService.notes.length > 70 
        ? this.integrationService.notes.substring(0, 70) + '...'
        : this.integrationService.notes
    },

    /** Get accessible last run description */
    getLastRunAriaLabel(): string {
      if (!this.integrationService.stats?.lastRunAt) return 'Never run'
      const lastRun = new Date(this.integrationService.stats.lastRunAt)
      const now = new Date()
      const diffMs = now.getTime() - lastRun.getTime()
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
      
      if (diffHours > 24) {
        const diffDays = Math.floor(diffHours / 24)
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      } else if (diffMinutes > 0) {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
      } else {
        return 'Just now'
      }
    }
  }
}
</script>

<style scoped>
.integration-card {
  transition: all 0.2s ease;
  min-height: 90px;
  width: 100%;
  position: relative;
  border: 1px solid var(--global-border-color, #e5e7eb);
  border-radius: var(--radius, 8px) !important;
  background-color: var(--global-card-background, #ffffff) !important;
}

.integration-card:focus {
  outline: 2px solid var(--global-primary, #406A94);
  outline-offset: 2px;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.integration-card--disabled {
  opacity: 0.6;
}

/* Main Layout Container */
.landscape-layout {
  display: flex;
  min-height: 90px;
  height: 100%;
  overflow: hidden;
  border-radius: inherit;
  border-left: 4px solid transparent;
}

/* Status accent colours - wraps with card border-radius */
.landscape-layout--success {
  border-left-color: #059669;
}

.landscape-layout--warning {
  border-left-color: #ea580c;
}

.landscape-layout--grey {
  border-left-color: #9e9e9e;
}

/* Left Section: Sparkline */
.sparkline-section {
  flex-shrink: 0;
  width: 200px;
  border-right: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.sparkline-container {
  width: 100%;
  height: 100%;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  padding: 0px;
  background-color: var(--global-muted, #E0E0E0) !important;
}

.sparkline-chart-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px 0;
  min-height: 35px;
}

.sparkline-stats {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 38px;
  padding: 4px 8px;
  background-color: rgba(0, 0, 0, 0.02);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.sparkline-stat-item {
  text-align: center;
  flex: 1;
}

.sparkline-stat-value {
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 2px;
}

.sparkline-stat-label {
  font-size: 0.65rem;
  color: var(--v-text-base);
  opacity: 0.7;
  line-height: 1.1;
}

.sparkline-empty {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

/* Status color accent classes for sparkline-container (legacy - accent now on landscape-layout) */

/* Middle Section: Content */
.content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px 0px 8px 16px;
  min-width: 0;
}

/* Legacy border classes - accent now on landscape-layout */

.content-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 6px;
}

.title-section {
  flex: 1;
  min-width: 0;
}

.content-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin: 0px 16px 0px 8px;
}

.service-name-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 2px;
  gap: 24px; /* Increased gap for more space between system name and clock icon */
}

.service-title-container {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.service-name-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
}

.service-name {
  font-size: 1rem;
  font-weight: 500;
  color: var(--v-text-base);
  line-height: 1.2;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.service-description {
  font-size: 0.75rem;
  color: var(--v-text-base);
  opacity: 0.7;
  line-height: 1.3;
  margin-top: 2px;
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.last-run-indicator {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
}

.last-run-text {
  font-size: 0.75rem;
  color: var(--v-text-base);
  opacity: 0.7;
  white-space: nowrap;
  line-height: 1.1;
}

.url-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  gap: 12px;
  min-height: 40px;
  flex-shrink: 0;
}

.url-group {
  display: flex;
  align-items: center;
  border-radius: var(--radius-sm);
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  flex: 1;
  min-width: 0;
  height: 32px;
}

.url-switch-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 32px;
}

.integration-switch .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.integration-switch .v-input__slot {
  margin-bottom: 0;
}

.http-method-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  color: white !important;
  font-weight: 600;
  font-size: 0.7rem;
  min-width: 60px;
  flex-shrink: 0;
  border-radius: var(--radius-sm) 0 0 var(--radius-sm);
}

.http-method-primary {
  background-color: var(--global-primary) !important; /* Same color as active switch track */
  color: #fff !important;
}

.http-method-neutral {
  background-color: #475569 !important; /* Slate-600 */
  color: #fff !important;
}

.http-method-success {
  background-color: #059669 !important; /* Green-700 for consistency */
  color: #fff !important;
}

.http-method-warning {
  background-color: #ea580c !important; /* Orange-600 for consistency */
  color: #fff !important;
}

.http-method-grey {
  background-color: #757575 !important; /* Grey-500 */
  color: #fff !important;
}

.url-field {
  flex: 1;
  padding: 4px 10px;
  display: flex;
  align-items: center;
  min-width: 0;
  height: 100%;
}

.url-text {
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: var(--v-text-base);
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Direct to S3 indicator (non-endpoint style) */
.s3-indicator-wrapper {
  display: flex;
  align-items: center;
  min-height: 32px;
}

.s3-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 0;
  background: transparent;
  border: none;
  box-shadow: none;
  color: var(--global-muted-foreground, #475569);
  font-size: 0.75rem;
  border-radius: var(--radius-sm);
}

</style>

<!-- Unscoped styles for dark mode support -->
<style lang="scss">
/* Dark mode overrides for IntegrationCard */
.dark,
.theme--dark {
  .integration-card.v-card,
  .v-card.integration-card,
  .integration-card.v-card--outlined,
  .integration-card.v-card--flat {
    background-color: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .integration-card .sparkline-container,
  .integration-card .sparkline-container.v-sheet {
    background-color: rgba(255, 255, 255, 0.06) !important;
  }

  .integration-card .sparkline-stats {
    background-color: rgba(0, 0, 0, 0.3) !important;
    border-top-color: rgba(255, 255, 255, 0.08) !important;
  }

  .integration-card .sparkline-stat-label {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .integration-card .sparkline-section {
    border-right-color: rgba(255, 255, 255, 0.08) !important;
  }

  .integration-card .service-name {
    color: var(--global-foreground, #f5f5f5) !important;
  }

  .integration-card .service-description {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .integration-card .last-run-text,
  .integration-card .last-run-indicator {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .integration-card .url-group {
    border-color: var(--global-border-color, #2e2e32) !important;
    box-shadow: none !important;
    background-color: rgba(255, 255, 255, 0.04) !important;
  }

  .integration-card .url-field {
    background-color: transparent !important;
  }

  .integration-card .url-text {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .integration-card .s3-indicator {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .integration-card:focus {
    outline-color: var(--global-primary, #ed303e) !important;
  }
}

/* Additional specificity for Vuetify theme */
.theme--dark .integration-card.v-card.v-sheet,
.dark .integration-card.v-card.v-sheet {
  background-color: var(--global-card-background, #1c1c1e) !important;
}
